'use client';
import type { JSX } from 'react';

import clsx from 'clsx';

import { backgroundColorClasses } from '@/components/classes/backgroundColorClasses';
import { testIds } from '@/components/constants/testIds';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import { composeSizeClasses } from '@/components/functions/composeSizeClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { Color } from '@/components/types/Color';
import type { Radius } from '@/components/types/Radius';
import type { Size } from '@/components/types/Size';
import type { TextSize } from '@/components/types/TextSize';
import Text from '@/components/ui/Text';
import { componentsConfig } from '@/config/componentsConfig';
import appIcons from '@/constants/appIcons';

import Icon from '../Icon';
import VStack from '../VStack';

type NewCheckboxProps = {
    value: boolean;
    onChange: (value: boolean) => void;
    label?: string | JSX.Element;
    labelSize?: Adaptive<TextSize>;
    labelColor?: Color;
    footnote?: string | JSX.Element;
    footnoteSize?: Adaptive<TextSize>;
    footnoteColor?: Color;
    size?: Adaptive<Size>;
    iconSize?: Adaptive<Size>;
    color?: Color;
    radius?: Adaptive<Radius>;
    isDisabled?: boolean;
    errorMessage?: string;
    className?: string;
    testId?: string;
};

export default function NewCheckbox({
    value = false,
    onChange,
    size = componentsConfig.checkbox.size,
    iconSize = componentsConfig.checkbox.iconSize,
    color = componentsConfig.checkbox.color,
    radius = componentsConfig.checkbox.radius,
    label,
    labelSize = componentsConfig.checkbox.labelSize,
    labelColor = componentsConfig.checkbox.labelColor,
    footnote,
    footnoteSize = componentsConfig.checkbox.footnoteSize,
    footnoteColor = componentsConfig.checkbox.footnoteColor,
    isDisabled,
    errorMessage,
    className,
    testId = testIds.checkBox.base,
}: NewCheckboxProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const commonClasses = 'flex justify-center items-center text-white p-1';

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <>
            <button
                type="button"
                disabled={isDisabled}
                className={clsx('flex items-start gap-x-2 disabled:opacity-50', className)}
                data-testid={testId}
                onClick={() => onChange(value)}
            >
                <div
                    className={clsx(
                        commonClasses,
                        composeSizeClasses(size),
                        value
                            ? backgroundColorClasses[color]
                            : 'flex-none bg-slate-100 dark:bg-neutral-700 border border-slate-200 dark:border-neutral-600',
                        composeRadiusClasses(radius)
                    )}
                    data-testid={testIds.checkBox.iconBox}
                >
                    {value ? <Icon icon={appIcons.solid.check} size={iconSize} color="white" testId={testIds.checkBox.icon} /> : null}
                </div>

                <VStack>
                    {typeof label === 'string' ? (
                        <Text size={labelSize} color={labelColor} testId={testIds.checkBox.text} className="-mt-0.5">
                            {label}
                        </Text>
                    ) : (
                        label
                    )}

                    {typeof footnote === 'string' ? (
                        <Text size={footnoteSize} color={footnoteColor} testId={testIds.checkBox.footnote}>
                            {footnote}
                        </Text>
                    ) : (
                        footnote
                    )}
                </VStack>
            </button>

            {/* Validation Error */}
            {errorMessage && (
                <Text size="2xs" color="danger" testId={testIds.numberInput.text}>
                    {errorMessage}
                </Text>
            )}
        </>
    );
}
